import { useMemo, memo } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import countryAddressDetails from 'postal-address-field-names';
import { UseFormReturn } from 'react-hook-form';
import useGeolocation from '@website/hooks/geoLocation/useGeolocation';

/**
 * Country options for select field
 *
 * The value is the country ISO (i.e. this will be sent in the mutation), but the label is the full country name.
 * Memoized as building the options can take a few ms on each render (and there are no props).
 */
export const CountryOptions = memo(function CountryOptions() {
  const options = countryAddressDetails.map((country) => {
    return (
      <option value={country.iso} key={country.iso}>
        {country.name}
      </option>
    );
  });

  return <>{options}</>;
});

/**
 * Address Field
 *
 * Bootstrap address fields for use with react-hook-form, that match the Skyhook
 * API AddressInput. The fields & labels are changed dynamically depending on
 * which country is selected (e.g. it shows Postal code for the UK and ZIP code
 * for the US). Note that additionalNames, organisation and sortingCode optional
 * fields have been removed.
 *
 * NB: The default country should be provided within your form's default values. Often it is best to do this using the
 * geo-location available from the global `Store`.
 *
 * @example
 * // Create default values for react-hook-form
 * const { state } = useContext(Store);
 * const defaultValues = {
 *     address: {
 *       country: geoLocation?.country?.code || 'GB',
 *     },
 *   };
 *
 * Autocomplete fields added from
 * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
 *
 */
export default function Address({
  name,
  register,
  watch,
  localityRequired = false,
  postalCodeRequired = false,
  showNameFields = true,
  isDisabled = false,
}: AddressFieldProps) {
  const geoLocation = useGeolocation();

  const country = watch(`${name}.country`);

  // Get the country-specific address fields
  const countryOptions = useMemo(() => {
    // fallback to GeoLocation Country or GB if Form Lacks Country Default Value
    const compareCountry = country ?? (geoLocation?.country?.code || 'GB');
    return countryAddressDetails.find((i) => i.iso === compareCountry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  return (
    <Form.Group>
      {showNameFields && (
        <Row>
          <Col>
            <Form.Label htmlFor={`${name}.givenName`}>First Name</Form.Label> *
            <Form.Control
              autoComplete="given-name"
              minLength={2}
              maxLength={35}
              id={`${name}.givenName`}
              {...register(`${name}.givenName`)}
              required
              disabled={isDisabled}
            />
          </Col>

          <Col>
            <Form.Label htmlFor={`${name}.familyName`}>Last Name</Form.Label> *
            <Form.Control
              autoComplete="family-name"
              minLength={2}
              maxLength={35}
              id={`${name}.familyName`}
              {...register(`${name}.familyName`)}
              required
              disabled={isDisabled}
            />
          </Col>
        </Row>
      )}
      {/* Country must come first as the form will then render other fields depending on this */}
      <Form.Label htmlFor={`${name}.country`}>Country</Form.Label> *
      <Form.Select
        autoComplete="country"
        id={`${name}.country`}
        {...register(`${name}.country`)}
        disabled={isDisabled}
      >
        <CountryOptions />
      </Form.Select>
      {!!countryOptions.fields.addressLine1 && (
        <>
          <Form.Label htmlFor={`${name}.addressLine1`}>
            {countryOptions.fields.addressLine1}
          </Form.Label>{' '}
          *
          <Form.Control
            autoComplete="address-line1"
            id={`${name}.addressLine1`}
            {...register(`${name}.addressLine1`)}
            required
            disabled={isDisabled}
          />
        </>
      )}
      {!!countryOptions.fields.addressLine2 && (
        <>
          <Form.Label htmlFor={`${name}.addressLine1`}>
            {countryOptions.fields.addressLine2}
          </Form.Label>
          <Form.Control
            autoComplete="address-line2"
            id={`${name}.addressLine2`}
            {...register(`${name}.addressLine2`)}
            disabled={isDisabled}
          />
        </>
      )}
      {!!countryOptions.fields.dependentLocality && (
        <>
          <Form.Label htmlFor={`${name}.dependentLocality`}>
            {countryOptions.fields.dependentLocality}
          </Form.Label>
          <Form.Control
            autoComplete="address-level3" // Note that autoComplete levels go in reverse order to lines
            id={`${name}.dependentLocality`}
            {...register(`${name}.dependentLocality`)}
            disabled={isDisabled}
          />
        </>
      )}
      {(!!countryOptions.fields.locality || localityRequired) && (
        <>
          <Form.Label htmlFor={`${name}.locality`}>
            {countryOptions.fields.locality}
          </Form.Label>
          {localityRequired && ' *'}
          <Form.Control
            autoComplete="address-level2"
            id={`${name}.locality`}
            {...register(`${name}.locality`)}
            required={localityRequired}
            disabled={isDisabled}
          />
        </>
      )}
      {!!countryOptions.fields.administrativeArea && (
        <>
          <Form.Label htmlFor={`${name}.administrativeArea`}>
            {countryOptions.fields.administrativeArea}
          </Form.Label>
          <Form.Control
            autoComplete="address-level1"
            id={`${name}.administrativeArea`}
            {...register(`${name}.administrativeArea`)}
            disabled={isDisabled}
          />
        </>
      )}
      {(!!countryOptions.fields.postalCode || postalCodeRequired) && (
        <>
          <Form.Label htmlFor={`${name}.postalCode`}>
            {countryOptions.fields.postalCode}
          </Form.Label>
          <span> *</span>
          <Form.Control
            autoComplete="postal-code"
            id={`${name}.postalCode`}
            {...register(`${name}.postalCode`)}
            required
            disabled={isDisabled}
          />
        </>
      )}
    </Form.Group>
  );
}

/**
 * Address field props
 */
export interface AddressFieldProps {
  /** If the locality (city) field is required */
  localityRequired?: boolean;

  /** If the postal code field is required */
  postalCodeRequired?: boolean;

  /** Field name prefix (e.g. if `address` then the ref of the postalCode month will be `address.postalCode`) */
  name: string;
  /** React hook form register */
  register: UseFormReturn<any>['register'];

  /** React hook form watch */
  watch: UseFormReturn<any>['watch'];

  /** Show the first & last name fields */
  showNameFields?: boolean;

  /** Can be used to disable all address fields */
  isDisabled?: boolean;
}

/**
 * Address submit values
 *
 * Helper type for use in `onSubmit` handler
 */
export interface AddressSubmitValues {
  givenName: string;
  familyName: string;
  country: string;
  addressLine1: string;
  addressLine2?: string;
  dependentLocality?: string;
  locality?: string;
  administrativeArea?: string;
  postalCode?: string;
}
